/**
 * Front-screen music, one track per mood of the twelve front pictures (front/scenes/index.ts): written in
 * jianpu for C0's own band (guzheng, guqin, dizi, xiao, erhu, drums, bells), so nothing is sampled and
 * nothing is copied: the tunes are original five-note lines. Section = 4 bars of 4 beats; the loop is
 * A B A B'. `login` (the island) is the existing track. Every track gets the lofi finish (lofi-front.ts).
 */
import { drone } from '../compose';
import { registerTrack } from '../registry';
import type { TrackDef } from '../types';
import { lofify, type LofiBeat } from './lofi-front';

const two = (a: string, b: string, c: string, d: string) => [a, b, c, d].join(' | ');

type Mood = Omit<TrackDef, 'sections' | 'form' | 'loop' | 'meter' | 'intro'> & {
  sections: Record<string, { parts: Record<string, string> }>;
};
/** The lofi finish of each mood: tempo and the kind of beat (lofi-front.ts). */
const FINISH: Record<string, { bpm: number; beat: LofiBeat }> = {
  fstorm: { bpm: 78, beat: 'boom' },
  fwar: { bpm: 66, beat: 'dusk' },
  fmoon: { bpm: 60, beat: 'soft' },
  fcalm: { bpm: 72, beat: 'boom' },
  fbells: { bpm: 58, beat: 'soft' },
  fnight: { bpm: 56, beat: 'soft' },
  frain: { bpm: 68, beat: 'rain' },
  fstele: { bpm: 54, beat: 'soft' },
};
const track = (m: Mood, form: string[]): TrackDef =>
  registerTrack(lofify({
    ...m,
    meter: 4,
    loop: true,
    form,
    sections: Object.fromEntries(Object.entries(m.sections).map(([k, s]) => [k, { bars: 4, parts: s.parts }])),
  }, FINISH[m.id]!));

const BEAT = `D_ 0_ d_ D_ 0_ D_ d_ d_`;
const FILL = `D_ d_ d_ D_ d_ d_ D_ D_`;
const HALF = `D- 0 d`;

// 1. Storm (ศึกกลางพายุ, ยอดเขาหิมะ): an erhu line over tremolo zheng and driving drums.
track(
  {
    id: 'fstorm',
    order: 40,
    title: 'ฟ้าคะนองบนหน้าผา',
    mood: 'ตึงเครียด ลมฟ้ากรรโชก',
    use: 'หน้าแรก: ศึกกลางพายุ, ยอดเขาหิมะ',
    key: 'D',
    mode: 'yu',
    bpm: 96,
    seed: 4001,
    humanize: 0.3,
    parts: {
      erhu: { inst: 'erhu', gain: 0.9, pan: 0.15, reverb: 0.35 },
      zheng: { inst: 'zheng', gain: 0.8, pan: -0.15, reverb: 0.3 },
      pad: { inst: 'pad', gain: 0.6, reverb: 0.4 },
      drums: { inst: 'perc', gain: 0.5, reverb: 0.25 },
    },
    sections: {
      A: {
        parts: {
          erhu: two(`@mf 6, 1 2 3`, `5- 3_ 2_ 1`, `2. 3_ 5 6`, `6--- ;`),
          zheng: two(`@mp 6,%---`, `3,%---`, `5,%---`, `6,%---`),
          pad: drone(`6,, 3,, 5,, 6,,`, 4),
          drums: two(BEAT, BEAT, BEAT, FILL),
        },
      },
      B: {
        parts: {
          erhu: two(`@f 6- 1' 6`, `5. 6_ 5 3`, `2_ 3_ 5_ 3_ 2 1`, `6,--- ;`),
          zheng: two(`@mf 6,_ 1_ 2_ 3_ 5_ 3_ 2_ 1_`, `6,_ 1_ 2_ 3_ 5_ 3_ 2_ 1_`, `5,_ 6,_ 1_ 2_ 3_ 2_ 1_ 6,_`, `6,%---`),
          pad: drone(`6,, 6,, 5,, 6,,`, 4),
          drums: two(BEAT, FILL, BEAT, `G--- `),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

// 2. War (ป้อมสำนักดาบโลหิต, ค่ายองครักษ์): slow war drums, a low zheng ostinato, one long erhu note.
track(
  {
    id: 'fwar',
    order: 41,
    title: 'กลองศึกใต้ธงแดง',
    mood: 'หนักแน่น ข่มขวัญ',
    use: 'หน้าแรก: ป้อมสำนักดาบโลหิต, ค่ายทางโคมเหล็ก',
    key: 'D',
    mode: 'yu',
    bpm: 72,
    seed: 4002,
    humanize: 0.2,
    parts: {
      drums: { inst: 'perc', gain: 0.65, reverb: 0.3 },
      zheng: { inst: 'zheng', gain: 0.7, pan: -0.1, reverb: 0.25 },
      erhu: { inst: 'erhu', gain: 0.7, pan: 0.2, reverb: 0.45 },
      pad: { inst: 'pad', gain: 0.65, reverb: 0.4 },
    },
    sections: {
      A: {
        parts: {
          drums: two(`D- 0 D`, `0- D d`, `D- 0 D`, `D_ d_ D_ d_ D G`),
          zheng: two(`@mp 6,,_ 6,,_ 0 6,,_ 6,,_ 0`, `6,,_ 6,,_ 0 3,,_ 3,,_ 0`, `6,,_ 6,,_ 0 5,,_ 5,,_ 0`, `6,,_ 6,,_ 6,, 0-`),
          erhu: two(`@mp 0---`, `6,--- ;`, `0---`, `1--- ;`),
          pad: drone(`6,, 3,, 5,, 6,,`, 4),
        },
      },
      B: {
        parts: {
          drums: two(`D_ D_ d_ d_ D_ d_ d_ D_`, `D- 0 D`, `D_ D_ d_ d_ D_ d_ d_ D_`, `D- D- `),
          zheng: two(`@mf 6,_ 1_ 2_ 1_ 6,_ 1_ 2_ 3_`, `5,_ 3_ 2_ 1_ 6,_ 1_ 6,_ 5,_`, `6,_ 1_ 2_ 1_ 6,_ 1_ 2_ 3_`, `6,%---`),
          erhu: two(`@mf 6- 5_ 6_ 1'`, `2'- 1' 6`, `6- 5_ 6_ 1'`, `6--- ;`),
          pad: drone(`6,, 6,, 5,, 6,,`, 4),
        },
      },
    },
  },
  ['A', 'A', 'B', 'A', 'B'],
);

// 3. Moon (จันทร์เพ็ญบนหลังคา, หุบเขาจันทร์มัว): guqin and a dizi, slow, wide.
track(
  {
    id: 'fmoon',
    order: 42,
    title: 'จันทร์เพ็ญดาบเดียวดาย',
    mood: 'เงียบ ตึงๆ เย็นเยียบ',
    use: 'หน้าแรก: จันทร์เพ็ญบนหลังคา, หุบเขาจันทร์มัว',
    key: 'F',
    mode: 'yu',
    bpm: 58,
    seed: 4003,
    humanize: 0.55,
    parts: {
      qin: { inst: 'qin', pan: 0.05, reverb: 0.55 },
      dizi: { inst: 'dizi', gain: 0.8, pan: -0.2, reverb: 0.6 },
      pad: { inst: 'pad', gain: 0.5, reverb: 0.6 },
      bowl: { inst: 'perc', gain: 0.25, pan: 0.3, reverb: 0.7 },
    },
    sections: {
      A: {
        parts: {
          qin: two(`@mp 6,,- 3,-`, `5,,- 2,/-`, `6,,~- 1,-`, `6,,~---`),
          dizi: two(`0---`, `@p 0- 6- ;`, `3. 5_ 6 5`, `3--- ;`),
          pad: drone(`6,, 5,, 6,, 6,,`, 4),
          bowl: two(`b---`, `0---`, `0---`, `0---`),
        },
      },
      B: {
        parts: {
          qin: two(`@mp 3'* 2'* 6* 0`, `5'*- 3'*-`, `2'*_ 3'*_ 2'*_ 1'*_ 6*-`, `6*---`),
          dizi: two(`@mp 6- 1' 6`, `5- 3 2`, `3. 5_ 3 2`, `1--- ;`),
          pad: drone(`6,, 3,, 5,, 6,,`, 4),
          bowl: two(`0---`, `0---`, `b---`, `0---`),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

// 4. Calm (ภูเขายอดหมอก): zheng arpeggios and a xiao, bright and open.
track(
  {
    id: 'fcalm',
    order: 43,
    title: 'หมอกบางเหนือยอดเขา',
    mood: 'สงบ โปร่ง ยามรุ่งอรุณ',
    use: 'หน้าแรก: ภูเขายอดหมอก',
    key: 'G',
    mode: 'gong',
    bpm: 66,
    seed: 4004,
    humanize: 0.5,
    parts: {
      zheng: { inst: 'zheng', pan: -0.1, reverb: 0.45 },
      xiao: { inst: 'xiao', gain: 0.85, pan: 0.2, reverb: 0.6 },
      pad: { inst: 'pad', gain: 0.45, reverb: 0.6 },
    },
    sections: {
      A: {
        parts: {
          zheng: two(`@mp 1_ 3_ 5_ 3_ 1_ 3_ 5_ 6_`, `2_ 5_ 6_ 5_ 2_ 5_ 6_ 5_`, `1_ 3_ 5_ 3_ 1_ 3_ 5_ 3_`, `6,_ 1_ 3_ 1_ 6,-`),
          xiao: two(`0---`, `@p 0- 5- ;`, `3. 5_ 6 5`, `3--- ;`),
          pad: drone(`1, 2, 1, 6,,`, 4),
        },
      },
      B: {
        parts: {
          zheng: two(`@mp 6,_ 1_ 3_ 1_ 6,_ 1_ 3_ 5_`, `5,_ 2_ 5_ 2_ 5,_ 2_ 5_ 6_`, `6,_ 1_ 3_ 5_ 6_ 5_ 3_ 1_`, `1%---`),
          xiao: two(`@mp 1'. 6_ 5 3`, `5- 3 2`, `3_ 5_ 6_ 5_ 3 2`, `1--- ;`),
          pad: drone(`6,, 5,, 6,, 1,`, 4),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

// 5. Bells (วัดหินระฆัง): the temple bell and the singing bowl, a xiao over a pad.
track(
  {
    id: 'fbells',
    order: 44,
    title: 'ระฆังเช้าเขาพันบันได',
    mood: 'ศักดิ์สิทธิ์ สงบ ก้องกังวาน',
    use: 'หน้าแรก: วัดหินระฆัง',
    key: 'C',
    mode: 'gong',
    bpm: 54,
    seed: 4005,
    humanize: 0.4,
    parts: {
      bell: { inst: 'perc', gain: 0.5, reverb: 0.75 },
      xiao: { inst: 'xiao', gain: 0.8, pan: -0.15, reverb: 0.65 },
      zheng: { inst: 'zheng', gain: 0.6, pan: 0.15, reverb: 0.5 },
      pad: { inst: 'pad', gain: 0.55, reverb: 0.6 },
    },
    sections: {
      A: {
        parts: {
          bell: two(`B---`, `0--- `, `b---`, `0---`),
          xiao: two(`0---`, `@mp 5,- 1 2`, `3- 2 1`, `5,--- ;`),
          zheng: two(`@p 1_ 5_ 1'_ 5_ 1_ 5_ 1'_ 5_`, `1_ 5_ 1'_ 5_ 1_ 5_ 1'_ 5_`, `6,_ 3_ 6_ 3_ 6,_ 3_ 6_ 3_`, `5,_ 2_ 5_ 2_ 5,-`),
          pad: drone(`1,, 1,, 6,, 5,,`, 4),
        },
      },
      B: {
        parts: {
          bell: two(`B---`, `0---`, `k_ k_ k_ k_ k-`, `B---`),
          xiao: two(`@mp 3. 5_ 6 5`, `3- 2 1`, `2. 3_ 5 3`, `1--- ;`),
          zheng: two(`@p 1_ 5_ 1'_ 5_ 1_ 5_ 1'_ 5_`, `6,_ 3_ 6_ 3_ 6,_ 3_ 6_ 3_`, `5,_ 2_ 5_ 2_ 5,_ 2_ 5_ 2_`, `1,_ 5_ 1_ 5_ 1,-`),
          pad: drone(`1,, 6,, 5,, 1,,`, 4),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

// 6. Night (ค่าย/หุบเขา by water and crickets): a quiet qin, a dizi, crickets and water in the percussion.
track(
  {
    id: 'fnight',
    order: 45,
    title: 'ธารใต้จันทร์มัว',
    mood: 'เงียบ ลึกลับ เสียงจิ้งหรีด',
    use: 'หน้าแรก: หุบเขาจันทร์มัว',
    key: 'A',
    mode: 'yu',
    bpm: 52,
    seed: 4006,
    humanize: 0.6,
    parts: {
      qin: { inst: 'qin', pan: -0.05, reverb: 0.6 },
      dizi: { inst: 'dizi', gain: 0.7, pan: 0.2, reverb: 0.65 },
      pad: { inst: 'pad', gain: 0.5, reverb: 0.6 },
      night: { inst: 'perc', gain: 0.18, reverb: 0.5 },
    },
    sections: {
      A: {
        parts: {
          qin: two(`@mp 6,,~--- `, `3,,/-- 5,,`, `6,,~---`, `2,,/-- 3,,`),
          dizi: two(`0---`, `0---`, `@p 6. 5_ 3 2`, `3--- ;`),
          pad: drone(`6,, 3,, 5,, 6,,`, 4),
          night: two(`n---`, `0---`, `n---`, `0---`),
        },
      },
      B: {
        parts: {
          qin: two(`@mp 3'* 2'* 6* 0`, `5'*- 3'*-`, `2'*_ 3'*_ 2'*_ 1'*_ 6*-`, `6*---`),
          dizi: two(`@mp 6- 1' 6`, `5- 3 2`, `3. 5_ 3 2`, `6,--- ;`),
          pad: drone(`6,, 5,, 6,, 6,,`, 4),
          night: two(`0---`, `n---`, `0---`, `n---`),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

// 7. Rain (หอหมึกสน, ป่าไผ่ในสายฝน): a dizi and a soft zheng with a wooden fish dropping like rain.
track(
  {
    id: 'frain',
    order: 46,
    title: 'ฝนพรำในป่าสน',
    mood: 'เหงา ละเมียด หม่นเย็น',
    use: 'หน้าแรก: หอหมึกสน, ป่าไผ่ในสายฝน',
    key: 'D',
    mode: 'zhi',
    bpm: 70,
    seed: 4007,
    humanize: 0.55,
    parts: {
      dizi: { inst: 'dizi', gain: 0.85, pan: 0.15, reverb: 0.55 },
      zheng: { inst: 'zheng', gain: 0.75, pan: -0.15, reverb: 0.45 },
      pad: { inst: 'pad', gain: 0.45, reverb: 0.55 },
      wood: { inst: 'perc', gain: 0.3, reverb: 0.4 },
    },
    sections: {
      A: {
        parts: {
          dizi: two(`@mp 5. 6_ 1' 6`, `5- 3 2`, `3. 5_ 6 5`, `3--- ;`),
          zheng: two(`@p 1_ 2_ 3_ 5_ 3_ 2_ 1_ 2_`, `6,_ 1_ 2_ 3_ 2_ 1_ 6,_ 1_`, `1_ 2_ 3_ 5_ 3_ 2_ 1_ 2_`, `5,_ 6,_ 1_ 2_ 1-`),
          pad: drone(`1,, 6,, 1,, 5,,`, 4),
          wood: two(`k_ 0_ k_ 0_ 0_ k_ 0_ k_`, `0_ k_ 0_ 0_ k_ 0_ k_ 0_`, `k_ 0_ k_ 0_ 0_ k_ 0_ k_`, `0--- `),
        },
      },
      B: {
        parts: {
          dizi: two(`@mp 6- 1' 2'`, `3'- 2' 1'`, `6. 5_ 3 5`, `6--- ;`),
          zheng: two(`@p 6,_ 1_ 2_ 1_ 6,_ 1_ 2_ 3_`, `5,_ 6,_ 1_ 2_ 5,_ 6,_ 1_ 2_`, `6,_ 1_ 2_ 3_ 2_ 1_ 6,_ 1_`, `1%---`),
          pad: drone(`6,, 5,, 6,, 1,,`, 4),
          wood: two(`0_ k_ 0_ k_ k_ 0_ 0_ k_`, `k_ 0_ 0_ k_ 0_ k_ k_ 0_`, `0_ k_ 0_ k_ k_ 0_ 0_ k_`, `0--- `),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

// 8. The stele (ศิลาเก้าร่อง): a low breathing drone, a slow heartbeat, harmonics like sparks, a bowl.
track(
  {
    id: 'fstele',
    order: 47,
    title: 'ศิลาร้อนดุจเลือด',
    mood: 'ลึกลับ หนักอึ้ง คาดเดาไม่ได้',
    use: 'หน้าแรก: ศิลาเก้าร่อง',
    key: 'D',
    mode: 'yu',
    bpm: 48,
    seed: 4008,
    humanize: 0.6,
    parts: {
      pad: { inst: 'pad', gain: 0.8, reverb: 0.7 },
      qin: { inst: 'qin', gain: 0.85, pan: 0.1, reverb: 0.7 },
      heart: { inst: 'perc', gain: 0.4, reverb: 0.5 },
      bowl: { inst: 'perc', gain: 0.25, pan: -0.3, reverb: 0.8 },
    },
    sections: {
      A: {
        parts: {
          pad: drone(`6,, 6,, 5,, 6,,`, 4),
          qin: two(`@p 6'* 0 3'* 0`, `2'*- 6*-`, `0--- `, `1'*- 6*-`),
          heart: two(`D 0 D 0`, `D 0 D 0`, `D 0 D 0`, `D 0 D 0`),
          bowl: two(`b---`, `0---`, `0---`, `0---`),
        },
      },
      B: {
        parts: {
          pad: drone(`6,, 3,, 5,, 6,,`, 4),
          qin: two(`@mp 6,,- 3,-`, `5'*- 3'*-`, `6,,~---`, `6*---`),
          heart: two(`D 0 D 0`, `D 0 D D`, `D 0 D 0`, `D- 0 0`),
          bowl: two(`0---`, `0---`, `b---`, `0---`),
        },
      },
    },
  },
  ['A', 'B', 'A', 'B'],
);

void FILL;
void HALF;
